import React, { useMemo } from "react";
import ResponsiveTable from "components/admin/ResponsiveTable";
import DateText from "components/admin/DateText";
import ActionButton from "components/admin/ActionButton";
import { ColumnDef } from "types/admin";
import { GiveawayContest } from "types/giveaway";

interface GiveawayContestTableProps {
  contests: GiveawayContest[];
  loading?: boolean;
  emptyMessage?: string;
  /** The "previous contests" table shows a Reward column; the latest one doesn't. */
  showReward?: boolean;
  /** Header above the action column ("Button" on the latest table, blank below). */
  actionsHeader?: string;
  onViewDetails: (contest: GiveawayContest) => void;
  /**
   * Omitted by the archive table — no Winners button is rendered there, since
   * announcing is done from the Latest section.
   */
  onViewWinners?: (contest: GiveawayContest) => void;
}

/**
 * Contest table shared by the "Latest" and "Previous" sections.
 * Both variants differ only by the Reward column and the Winners action, so the
 * columns are built once here rather than duplicated per section.
 */
const GiveawayContestTable: React.FC<GiveawayContestTableProps> = ({
  contests,
  loading = false,
  emptyMessage = "No contests found",
  showReward = false,
  actionsHeader = "",
  onViewDetails,
  onViewWinners,
}) => {
  const columns = useMemo<ColumnDef<GiveawayContest>[]>(() => {
    const base: ColumnDef<GiveawayContest>[] = [
      {
        key: "contestName",
        header: "Contest Name",
        render: (item) => (
          <span className="font-medium text-grease-100">
            {item.contestName || "-"}
          </span>
        ),
      },
      {
        key: "startDate",
        header: "Start Date",
        render: (item) => <DateText value={item.startDate} />,
      },
      {
        key: "endDate",
        header: "End Date",
        render: (item) => <DateText value={item.endDate} />,
      },
    ];

    if (showReward) {
      base.push({
        key: "reward1st",
        header: "Reward",
        render: (item) => item.reward1st || "-",
      });
    }

    base.push({
      key: "status",
      header: "Status",
      render: (item) => item.status || "-",
    });

    return base;
  }, [showReward]);

  return (
    <ResponsiveTable<GiveawayContest>
      columns={columns}
      data={contests}
      loading={loading}
      emptyMessage={emptyMessage}
      rowKey={(item) => item.contestId}
      actionsHeader={actionsHeader}
      renderActions={(item) => {
        const canAddWinners =
          item.status === "Awaiting Result" || item.status === "Completed";
        const winnersTooltip = canAddWinners
          ? `View winners of ${item.contestName}`
          : "Winners can only be added after the contest ends";

        return (
          <>
            <ActionButton
              onClick={() => onViewDetails(item)}
              ariaLabel={`View details for ${item.contestName}`}
              className="flex-1 md:flex-none"
            >
              Contest Details
            </ActionButton>
            {onViewWinners && (
              <ActionButton
                onClick={() => onViewWinners(item)}
                disabled={!canAddWinners}
                title={winnersTooltip}
                ariaLabel={`View winners of ${item.contestName}`}
                className="flex-1 md:flex-none"
              >
                Winners
              </ActionButton>
            )}
          </>
        );
      }}
    />
  );
};

export default GiveawayContestTable;
